/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import React from "react";
import { FORM_ACCESSIBILITY_NODES_PREFIX } from "constants/constants";
import accessibilityStyles from "./Accessibility.css";
import { AccessibleButtonNode } from "./readOnlyFormNodes/AccessibleButtonNode";
import { AccessibleCheckBoxNode } from "./readOnlyFormNodes/AccessibleCheckBoxNode";
import { AccessibleRadioButtonNode } from "./readOnlyFormNodes/AccessibleRadioButtonNode";
import { AccessibleTextBoxNode } from "./readOnlyFormNodes/AccessibleTextBoxNode";
import { AccessibleComboBoxNode } from "./readOnlyFormNodes/AccessibleComboBoxNode";
import { AccessibleListBoxNode } from "./readOnlyFormNodes/AccessibleListBoxNode";
import { AccessibleBarcodeNode } from "./readOnlyFormNodes/AccessibleBarcodeNode";
import { AccessibleSignatureNode } from "./readOnlyFormNodes/AccessibleSignatureNode";
import { FIELD_TYPE } from "javascript/jsEngine/constants/enums";

const getNodeToRender = (annotInfo, style) => {
  switch (annotInfo.fieldType) {
    case FIELD_TYPE.TEXT:
    case FIELD_TYPE.MULTILINE_TEXT:
    case FIELD_TYPE.COMB:
    case FIELD_TYPE.RICH_TEXT:
    case FIELD_TYPE.PASSWORD:
    case FIELD_TYPE.ATTACHMENT:
    case FIELD_TYPE.DATE_TIME:
      return <AccessibleTextBoxNode annotInfo={annotInfo} style={style} />;
    case FIELD_TYPE.DROPDOWN:
      return <AccessibleComboBoxNode annotInfo={annotInfo} style={style} />;
    case FIELD_TYPE.RADIO:
      return <AccessibleRadioButtonNode annotInfo={annotInfo} style={style} />;
    case FIELD_TYPE.LIST:
      return <AccessibleListBoxNode annotInfo={annotInfo} style={style} />;
    case FIELD_TYPE.CHECKBOX:
      return <AccessibleCheckBoxNode annotInfo={annotInfo} style={style} />;
    case FIELD_TYPE.SIGNATURE:
      return <AccessibleSignatureNode annotInfo={annotInfo} style={style} />;
    case FIELD_TYPE.BARCODE:
      return <AccessibleBarcodeNode annotInfo={annotInfo} style={style} />;
    case FIELD_TYPE.BUTTON:
      return <AccessibleButtonNode annotInfo={annotInfo} style={style} />;
    default:
      return null;
  }
};

export const AccessibleFormNode = ({ style, parentRect, annotInfo, shouldRenderReadonlyFields }) => {
  if (!annotInfo) return null;
  // If formfields are inside table or list and rendered using acroform dropin, then we need to render them relative to page, not list item or table cell.
  const readOnlyFieldHasData = annotInfo.fieldType && annotInfo.formId && Object.keys(annotInfo).length > 2;

  // if some other client is going to render their own form-filling experience by modifying a11y tree, then they'll set enableFormFilling=false
  // enableFormFilling=false -> shouldRenderReadonlyFields=true -> readOnlyFields to be rendered,
  // if read-only fields not required by client, modify ally tree to not send anything other than formId and fieldType in annotInfo of parent node
  const allowReadOnlyFields = shouldRenderReadonlyFields && readOnlyFieldHasData;
  const usePagePositioning = parentRect && (!shouldRenderReadonlyFields || !allowReadOnlyFields);
  const pagePositioningStyle = usePagePositioning && {
    style: { position: "absolute", left: `${-1 * parentRect.left}px`, top: `${-1 * parentRect.top}px` }
  };
  const eventHandler = ev => {
    ev.stopPropagation();
  };
  return (
    annotInfo && (
      <div
        data-form-id={`${FORM_ACCESSIBILITY_NODES_PREFIX}-${annotInfo.formId}`}
        className={accessibilityStyles.formFieldContainer}
        {...pagePositioningStyle}
        onMouseDown={eventHandler}
        onMouseUp={eventHandler}
        onMouseMove={eventHandler}
        onClick={eventHandler}
        onDoubleClick={eventHandler}
        onTouchStart={eventHandler}
        onTouchEnd={eventHandler}
      >
        {allowReadOnlyFields && getNodeToRender(annotInfo, style)}
      </div>
    )
  );
};
